<template>
  <v-dialog
    v-model="dialog"
    max-width="800"
    :persistent="generating || hasSlotLoading"
    @input="onDialogChange"
  >
    <v-card>
      <v-card-title>Gerar imagem</v-card-title>
      <v-card-text>
        <v-row>
          <v-col cols="12" sm="6" md="5">
            <v-textarea
              v-model="content"
              label="Conteúdo da imagem"
              placeholder="Ex.: camiseta básica com logo"
              outlined
              dense
              rows="2"
              class="mb-3"
              :disabled="generating || hasSlotLoading"
              hint="Shift + Enter para gerar novamente"
              persistent-hint
              @keydown.shift.enter.prevent="onShiftEnterGenerate"
            />
            <v-radio-group
              v-model="imageCount"
              label="Quantidade de imagens"
              dense
              hide-details
              class="mb-2"
            >
              <v-radio :value="1" label="1 imagem" />
              <v-radio :value="2" label="2 imagens" />
              <v-radio :value="4" label="4 imagens" />
            </v-radio-group>

            <p class="text-body2 mb-2">
              Imagens de referência (opcional, até {{ maxReferenceImages }})
            </p>
            <v-slide-group v-if="referenceImages.length > 0" class="mb-2">
              <v-slide-item
                v-for="(item, index) in referenceImages"
                :key="`ref-${index}`"
              >
                <div class="reference-image-item mx-1">
                  <v-img
                    :src="item.url"
                    :aspect-ratio="1"
                    height="56"
                    width="56"
                    class="rounded image-clickable"
                    @click="openImagePreview(item.url)"
                  />
                  <v-btn
                    icon
                    x-small
                    color="error"
                    class="reference-remove-btn"
                    @click.stop="removeReference(index)"
                  >
                    <v-icon x-small>mdi-close</v-icon>
                  </v-btn>
                </div>
              </v-slide-item>
            </v-slide-group>
            <v-file-input
              :value="referenceFileInput"
              label="Adicionar referência"
              accept="image/*"
              prepend-icon="mdi-image-plus"
              outlined
              dense
              show-size
              hide-details
              :disabled="!canAddReference"
              class="mb-3"
              @change="onReferenceFileChange"
            />

            <v-btn
              color="primary"
              :loading="generating"
              :disabled="!content.trim()"
              @click="generate"
              block
              class="mb-2"
            >
              Gerar novamente
            </v-btn>
            <v-alert v-if="error" type="error" dense text class="mt-2">
              {{ error }}
            </v-alert>
          </v-col>
          <v-col cols="12" sm="6" md="7">
            <div
              class="image-preview-wrapper rounded-xl overflow-hidden"
              style="aspect-ratio: 1"
            >
              <template v-if="generating">
                <div
                  class="d-flex flex-column align-center justify-center fill-height placeholder-generating"
                >
                  <v-progress-circular
                    indeterminate
                    color="primary"
                    size="48"
                    class="mb-3"
                  />
                  <span class="text-body2"
                    >Gerando {{ imageCount }} image{{
                      imageCount > 1 ? "ns" : "m"
                    }}...</span
                  >
                </div>
              </template>
              <template v-else-if="hasSuggestions">
                <div
                  :class="[
                    'generated-grid',
                    imageCount === 1 ? 'grid-cols-1' : 'grid-cols-2',
                  ]"
                >
                  <div
                    v-for="(slot, idx) in suggestions"
                    :key="idx"
                    class="generated-image-item"
                  >
                    <template v-if="slot.loading">
                      <div
                        class="d-flex flex-column align-center justify-center fill-height slot-loading"
                      >
                        <v-progress-circular
                          indeterminate
                          color="primary"
                          size="32"
                        />
                        <span class="text-caption mt-2">Gerando...</span>
                      </div>
                    </template>
                    <template v-else-if="slot.error">
                      <div
                        class="d-flex flex-column align-center justify-center fill-height slot-error"
                      >
                        <v-icon color="error" size="32"
                          >mdi-alert-circle</v-icon
                        >
                        <span class="text-caption mt-2 text-center"
                          >Erro ao gerar</span
                        >
                        <v-btn
                          small
                          text
                          color="primary"
                          class="mt-2"
                          @click="regenerateSlot(idx)"
                        >
                          Tentar novamente
                        </v-btn>
                      </div>
                    </template>
                    <template v-else-if="slot.url">
                      <v-img
                        :src="slot.url"
                        :aspect-ratio="1"
                        class="fill-height image-clickable"
                        contain
                        @click="openImagePreview(slot.url)"
                      />
                      <v-tooltip v-if="slot.model" bottom>
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn
                            v-bind="attrs"
                            v-on="on"
                            icon
                            x-small
                            class="generated-image-model-btn"
                            @click.stop
                          >
                            <v-icon x-small>mdi-auto-fix</v-icon>
                          </v-btn>
                        </template>
                        <span>{{ slot.model }}</span>
                      </v-tooltip>
                      <div class="generated-image-actions" @click.stop>
                        <v-btn
                          color="primary"
                          icon
                          small
                          :disabled="!canAddReference || isReferenceSlot(idx)"
                          @click="addReferenceFromSuggestion(idx)"
                        >
                          <v-icon small>mdi-image-plus</v-icon>
                        </v-btn>
                        <v-btn
                          color="error"
                          icon
                          small
                          @click="discardImage(idx)"
                        >
                          <v-icon small>mdi-close</v-icon>
                        </v-btn>
                        <v-btn
                          color="success"
                          icon
                          small
                          @click="selectImage(idx)"
                        >
                          <v-icon small>mdi-check</v-icon>
                        </v-btn>
                      </div>
                    </template>
                  </div>
                </div>
              </template>
              <template v-else>
                <div
                  class="d-flex flex-column align-center justify-center fill-height placeholder-empty"
                >
                  <v-icon size="64" color="grey lighten-1">
                    mdi-image-off
                  </v-icon>
                  <span class="text-body2 mt-2">Nenhuma prévia</span>
                </div>
              </template>
            </div>

            <template v-if="selectedImages.length > 0">
              <p class="text-body2 mt-4 mb-2">
                Imagens selecionadas ({{ selectedImages.length }})
              </p>
              <v-slide-group class="mb-2">
                <v-slide-item
                  v-for="(item, index) in selectedImages"
                  :key="index"
                >
                  <div>
                    <v-hover v-slot:default="{ hover }">
                      <v-img
                        :src="item.url"
                        :aspect-ratio="1"
                        height="64"
                        width="64"
                        class="rounded mx-1 image-clickable"
                        @click="openImagePreview(item.url)"
                      >
                        <v-fade-transition>
                          <v-overlay v-if="hover" absolute>
                            <div class="d-flex">
                              <v-btn
                                icon
                                small
                                color="primary"
                                class="mx-1"
                                :disabled="!canAddReference || isReference(item.file)"
                                @click.stop="addReferenceFromSelected(index)"
                              >
                                <v-icon small>mdi-image-plus</v-icon>
                              </v-btn>
                              <v-btn
                                icon
                                small
                                color="error"
                                class="mx-1"
                                @click.stop="askRemoveImage(index)"
                              >
                                <v-icon small>mdi-delete</v-icon>
                              </v-btn>
                            </div>
                          </v-overlay>
                        </v-fade-transition>
                      </v-img>
                    </v-hover>
                  </div>
                </v-slide-item>
              </v-slide-group>
            </template>
          </v-col>
        </v-row>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn text @click="close">Cancelar</v-btn>
        <v-btn
          color="primary"
          :disabled="selectedImages.length === 0"
          @click="confirm"
        >
          Usar image{{ selectedImages.length > 1 ? "ns" : "m" }}
        </v-btn>
      </v-card-actions>
    </v-card>

    <v-dialog
      v-model="imagePreviewDialog"
      @click:outside="imagePreviewDialog = false"
      width="calc(90vh - 48px)"
      content-class="rounded-xl"
    >
      <v-img
        v-if="imagePreviewUrl"
        @click="imagePreviewDialog = false"
        :src="imagePreviewUrl"
        contain
        max-height="85vh"
        class="ma-0"
        :aspect-ratio="1"
      />
    </v-dialog>

    <v-dialog v-model="confirmRemoveDialog" max-width="400" persistent>
      <v-card>
        <v-card-title>Remover imagem?</v-card-title>
        <v-card-text>
          Esta imagem será removida da seleção. Deseja continuar?
          <v-img
            :src="selectedImages[indexToRemove]?.url"
            :aspect-ratio="1"
            class="rounded-lg mt-2 px-0"
          />
        </v-card-text>
        <v-card-actions>
          <v-btn text @click="confirmRemoveDialog = false">Cancelar</v-btn>
          <v-spacer />
          <v-btn color="error" text @click="removeImage">Remover</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-dialog>
</template>

<script>
import merchProductsService from "@/services/admin/merchProducts";
import ImageConverter from "@/utils/ImageConverter";

const MAX_REFERENCE_IMAGES = 2;
const REFERENCE_COMPRESS_SETTINGS = {
  maxWidth: 1024,
  maxHeight: 1024,
  outputType: "image/webp",
  quality: 0.92,
};

function base64ToFile(base64, mimeType, filename = "generated.png") {
  const binary = atob(base64);
  const arr = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) arr[i] = binary.charCodeAt(i);
  return new File([new Blob([arr], { type: mimeType })], filename, {
    type: mimeType,
  });
}

export default {
  name: "GenerateImageDialog",
  data() {
    return {
      dialog: false,
      productDetails: null,
      content: "",
      imageCount: 4,
      generating: false,
      error: null,
      suggestions: [],
      selectedImages: [],
      referenceImages: [],
      referenceFileInput: null,
      confirmRemoveDialog: false,
      indexToRemove: null,
      imagePreviewDialog: false,
      imagePreviewUrl: null,
    };
  },
  computed: {
    maxReferenceImages() {
      return MAX_REFERENCE_IMAGES;
    },
    canAddReference() {
      return this.referenceImages.length < MAX_REFERENCE_IMAGES;
    },
    hasSuggestions() {
      return this.suggestions.length > 0;
    },
    hasSlotLoading() {
      return this.suggestions.some((s) => s && s.loading);
    },
  },
  methods: {
    open(details = {}) {
      this.productDetails = details;
      this.content = [details.name, details.description]
        .filter(Boolean)
        .join(" - ");
      this.error = null;
      this.suggestions = [];
      this.selectedImages = [];
      this.referenceImages = [];
      this.referenceFileInput = null;
      this.dialog = true;
    },
    onShiftEnterGenerate() {
      if (!this.content.trim() || this.generating || this.hasSlotLoading) return;
      this.generate();
    },
    onDialogChange(open) {
      if (!open) this.emitConfirmIfNeeded();
    },
    async generate() {
      if (!this.content.trim()) return;
      if (this.generating || this.hasSlotLoading) return;
      this.generating = true;
      this.error = null;
      this.suggestions = [];
      try {
        const res = await merchProductsService.generateMerchProductImage({
          content: this.content.trim(),
          count: this.imageCount,
          referenceFiles: await this.getCompressedReferenceFiles(),
        });
        const images = res.images || [];
        this.suggestions = images.map((img) => ({
          url: `data:${img.mimeType || "image/png"};base64,${img.image}`,
          base64: img.image,
          mimeType: img.mimeType || "image/png",
          model: img.model || null,
        }));
      } catch (err) {
        this.error = err.message || "Erro ao gerar as imagens.";
      } finally {
        this.generating = false;
      }
    },
    async regenerateSlot(idx) {
      if (!this.content.trim()) return;
      this.$set(this.suggestions, idx, { loading: true });
      this.error = null;
      try {
        const res = await merchProductsService.generateMerchProductImage({
          content: this.content.trim(),
          count: 1,
          referenceFiles: await this.getCompressedReferenceFiles(),
        });
        const img = (res.images || [])[0];
        if (!img) throw new Error("Nenhuma imagem retornada.");
        this.$set(this.suggestions, idx, {
          url: `data:${img.mimeType || "image/png"};base64,${img.image}`,
          base64: img.image,
          mimeType: img.mimeType || "image/png",
          model: img.model || null,
        });
      } catch (err) {
        this.$set(this.suggestions, idx, { error: true });
        this.error = err.message || "Erro ao gerar a imagem.";
      }
    },
    selectImage(idx) {
      const slot = this.suggestions[idx];
      if (!slot?.base64) return;
      const file = base64ToFile(
        slot.base64,
        slot.mimeType,
        `gerada-${Date.now()}-${idx}.png`
      );
      this.selectedImages.push({
        file,
        url: slot.url,
        model: slot.model || null,
      });
      this.regenerateSlot(idx);
    },
    discardImage(idx) {
      this.regenerateSlot(idx);
    },
    openImagePreview(url) {
      this.imagePreviewUrl = url;
      this.imagePreviewDialog = true;
    },
    close() {
      this.dialog = false;
      this.emitConfirmIfNeeded();
    },
    confirm() {
      this.emitConfirmIfNeeded();
      this.dialog = false;
    },
    askRemoveImage(index) {
      this.indexToRemove = index;
      this.confirmRemoveDialog = true;
    },
    removeImage() {
      if (this.indexToRemove !== null) {
        this.selectedImages.splice(this.indexToRemove, 1);
        this.indexToRemove = null;
      }
      this.confirmRemoveDialog = false;
    },
    emitConfirmIfNeeded() {
      this.$emit("confirm", [...this.selectedImages]);
    },
    getReferenceFiles() {
      return this.referenceImages.map((item) => item.file);
    },
    async getCompressedReferenceFiles() {
      const files = this.getReferenceFiles().filter(Boolean);
      if (!files.length) return [];

      const imageConverter = new ImageConverter();
      return Promise.all(
        files.map((file) =>
          imageConverter.compressFile(file, REFERENCE_COMPRESS_SETTINGS)
        )
      );
    },
    isReference(file) {
      if (!file) return false;
      return this.referenceImages.some((item) => item.file === file);
    },
    isReferenceSlot(idx) {
      const slot = this.suggestions[idx];
      if (!slot?.url) return false;
      return this.referenceImages.some((item) => item.url === slot.url);
    },
    addReferenceEntry(file, url) {
      if (!file || !this.canAddReference || this.isReference(file)) return;
      if (this.referenceImages.some((item) => item.url === url)) return;
      this.referenceImages.push({ file, url });
    },
    addReferenceFile(file) {
      if (!file) return;
      this.addReferenceEntry(file, URL.createObjectURL(file));
    },
    onReferenceFileChange(files) {
      const selectedFiles = Array.isArray(files) ? files : files ? [files] : [];
      const remainingSlots = MAX_REFERENCE_IMAGES - this.referenceImages.length;

      selectedFiles.slice(0, remainingSlots).forEach((file) => {
        this.addReferenceFile(file);
      });

      this.referenceFileInput = null;
    },
    addReferenceFromSelected(index) {
      const item = this.selectedImages[index];
      if (!item?.file) return;
      this.addReferenceEntry(item.file, item.url);
    },
    addReferenceFromSuggestion(idx) {
      const slot = this.suggestions[idx];
      if (!slot?.base64) return;
      const file = base64ToFile(
        slot.base64,
        slot.mimeType,
        `referencia-${Date.now()}-${idx}.png`
      );
      this.addReferenceEntry(file, slot.url);
    },
    removeReference(index) {
      this.referenceImages.splice(index, 1);
    },
  },
};
</script>

<style scoped>
.gap-2 {
  gap: 8px;
}
.image-preview-wrapper {
  min-height: 280px;
  aspect-ratio: 1;
  background: #f5f5f5;
}
.generated-grid {
  display: grid;
  gap: 8px;
  height: 100%;
  padding: 8px;
}
.generated-grid.grid-cols-1 {
  grid-template-columns: 1fr;
}
.generated-grid.grid-cols-2 {
  grid-template-columns: 1fr 1fr;
}
.generated-image-item {
  position: relative;
  aspect-ratio: 1;
  overflow: hidden;
  border-radius: 8px;
  background: #fff;
}
.slot-loading,
.slot-error {
  min-height: 100%;
  padding: 16px;
}
.generated-image-item .v-image {
  width: 100%;
  height: 100%;
}
.image-clickable {
  cursor: pointer;
}
.generated-image-model-btn {
  position: absolute;
  top: 4px;
  left: 4px;
  z-index: 2;
  background: rgba(0, 0, 0, 0.5) !important;
}
.generated-image-actions {
  position: absolute;
  bottom: 8px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 4px;
  opacity: 0;
  transition: opacity 0.2s;
}
.generated-image-item:hover .generated-image-actions {
  opacity: 1;
}
.generated-image-actions .v-btn {
  background: rgba(255, 255, 255, 0.9) !important;
}
.reference-image-item {
  position: relative;
  display: inline-block;
}
.reference-remove-btn {
  position: absolute;
  top: -6px;
  right: -6px;
  background: rgba(255, 255, 255, 0.95) !important;
}
.placeholder-generating,
.placeholder-empty {
  min-height: 280px;
}
.selected-image-wrapper {
  position: relative;
  display: inline-block;
}
.selected-image-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
}
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.15s ease;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>
